import { useQuery } from "@apollo/client/react";
import {
  copyNotesToPatientRedirect,
  moveNotesToPatientRedirect,
} from "@components/common/multiselect/utils";
import { DSCircularButton } from "@components/design-system/molecules";
import { faClone } from "@fortawesome/free-regular-svg-icons";
import {
  faInfoCircle,
  faTrash,
  faTruckMoving,
  faTimes,
  faPeopleArrows,
  faUpRightFromSquare,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import getPatientsQuery from "@graphql/queries/GetPatients";
import {
  useNoteMultiselectContext,
  useNoteMultiselect,
} from "@hooks";
import { useTeam } from "@hooks";
import { Tooltip } from "@mui/material";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { config, animated, useTransition } from "react-spring";

import { MultiSelectActionButton } from "./MultiSelectActionButton";
import { TransferMultiModal } from "./TransferMultiModal";

export function MultiSelectActionContainer({ notes, refetch }) {
  const navigate = useNavigate();
  const { currentTeam } = useTeam();

  const {
    numSelectedNotes,
    selectedNoteUuids,
    setShouldShowDeleteNoteModal,
    shouldShowDeleteNoteModal,
    resetSelectedNotesList,
  } = useNoteMultiselectContext();

  const { shouldAllowCopyAction } = useNoteMultiselect({
    notes,
    refetch,
  });

  const [shouldShow, setShouldShow] = useState(false);
  const [isModalShowing, setIsModalShowing] = useState(false);
  const { transferNotes: isTransferNotesEnabled } = useFlags();

  const { data, loading } = useQuery(getPatientsQuery, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    setShouldShow(numSelectedNotes > 0);
  }, [numSelectedNotes]);

  useEffect(() => {
    resetSelectedNotesList();
  }, [currentTeam, resetSelectedNotesList]);

  const isPatientPage =
    window.location.pathname.includes("/notebook/");

  const transitions = useTransition(shouldShow, {
    from: { opacity: 0, y: 120 },
    enter: { opacity: 1, y: 0 },
    leave: { opacity: 0, y: 120 },
    config: config.default,
  });

  if (shouldShowDeleteNoteModal || loading) {
    return null;
  }

  return transitions((styles, item) =>
    item ? (
      <animated.div
        style={styles}
        className="fixed bottom-0 left-0 z-30 w-full flex flex-col items-center justify-center pointer-events-none"
      >
        <TransferMultiModal
          notes={notes}
          shouldShowTransferModal={isModalShowing}
          setShouldShowTransferModal={setIsModalShowing}
          refetch={refetch}
        />

        <div
          style={{
            boxShadow: "0px -5px 50px -10px rgba(0,0,0,0.40)",
          }}
          className="relative flex flex-col items-center bg-primary pt-4 pb-2 px-10 rounded-t-2xl pointer-events-auto"
        >
          <div className="absolute top-4 right-4">
            <DSCircularButton
              icon={faTimes}
              variant="danger"
              size="sm"
              onClick={resetSelectedNotesList}
              dataTestId="multiselect-close-button"
            />
          </div>
          <div className="flex flex-row items-center justify-between w-full">
            <h4 className="flex-1 text-center mb-3 text-secondary">
              ⚡️ {numSelectedNotes} note
              {numSelectedNotes > 1 ? "s" : ""} selected
            </h4>
          </div>
          <div className="flex flex-wrap md:flex-row space-x-2 space-y-2 md:space-y-0 items-center justify-center">
            {numSelectedNotes > 1 && (
              <Tooltip
                arrow
                title="Please ensure you have popups enabled or this feature will not work as expected."
              >
                <div>
                  <MultiSelectActionButton
                    onClickActionDescription="Open all in new tabs"
                    onClick={() => {
                      Object.keys(selectedNoteUuids).forEach(
                        (noteUuid) =>
                          window.open(
                            `/edit_note/${noteUuid}`,
                            "_blank",
                          ),
                      );
                    }}
                    icon={faUpRightFromSquare}
                  />
                </div>
              </Tooltip>
            )}
            {data?.patients?.length > 0 && (
              <MultiSelectActionButton
                onClickActionDescription="Move to patient"
                onClick={() =>
                  moveNotesToPatientRedirect({
                    selectedNoteUuids,
                    navigate,
                  })
                }
                icon={faTruckMoving}
                disabled={!shouldAllowCopyAction}
              />
            )}
            <MultiSelectActionButton
              onClickActionDescription="Duplicate to patient"
              onClick={() =>
                copyNotesToPatientRedirect({
                  selectedNoteUuids,
                  navigate,
                })
              }
              icon={faClone}
              disabled={!shouldAllowCopyAction}
            />
            {isTransferNotesEnabled && currentTeam?.uuid && (
              <MultiSelectActionButton
                onClickActionDescription="Transfer ownership"
                onClick={() => setIsModalShowing(true)}
                icon={faPeopleArrows}
                disabled={!shouldAllowCopyAction || isPatientPage}
              />
            )}
            <MultiSelectActionButton
              onClickActionDescription="Delete"
              onClick={() => setShouldShowDeleteNoteModal(true)}
              icon={faTrash}
              variant="danger"
            />
          </div>
          {!shouldAllowCopyAction && (
            <h5 className="text-xs text-placeholder pt-2 text-center">
              <FontAwesomeIcon icon={faInfoCircle} /> You can't move,
              duplicate, or transfer notes labeled
              &apos;Processing&apos; or &apos;New Recording&apos;.
            </h5>
          )}
          {isPatientPage && (
            <h5 className="text-xs text-placeholder pt-2 text-center">
              <FontAwesomeIcon icon={faInfoCircle} /> You can't
              transfer notes that are assigned to a patient.
            </h5>
          )}
        </div>
      </animated.div>
    ) : null,
  );
}
